import { useState, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";
import { Box, Group, Paper, Text, UnstyledButton } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import { ExpertiseChip, FocusSectorChip } from "@/components/ecosystem-head/FocusSectorChip";
import EcosystemHeadShortlistBookmarkButton from "@/pages/ecosystem-heads/components/EcosystemHeadShortlistBookmarkButton";
import SuggestionDismissButton, {
  SUGGESTION_CARD_ACTION_ATTR,
} from "@/pages/dashboard/components/SuggestionDismissButton";
import {
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { normalizeTargetCountryList } from "@/components/ecosystem-head/ecosystemHeadTargetCountries.util";
import { getIndustryLabel } from "@/components/shared/constants/general.const";
import type {
  EcosystemHeadFocusSector,
  EcosystemHeadProfileStatus,
} from "@shared/ecosystemHeadProfile";
import {
  formatJoinRequestSlotsLabel,
  ecosystemHeadExploreCtaLabel,
  isEcosystemHeadCorridor,
} from "@/pages/ecosystem-heads/ecosystemHeadCorridorCard.util";
import EcosystemExploreTypeBadgeRow from "@/pages/ecosystem-heads/components/EcosystemExploreTypeBadgeRow";
import CorridorPricingBadge from "@/components/ecosystem-head/CorridorPricingBadge";
import type { CorridorPricingBadge as CorridorPricingBadgeData } from "@shared/corridorPricingBadge";
import classes from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";

export type EcosystemHeadCorridorCardData = {
  id: string;
  name: string;
  corridorName?: string;
  badgeShow?: string | null;
  managedCompanyCount?: number | null;
  ecosystemIndustry: string | null;
  ecosystemCountry: string | null;
  ecosystemCity?: string | null;
  ecosystemTargetCountry: string | null;
  corridorLabel: string;
  ecosystemHeadSummary?: string | null;
  ecosystemHeadLiveFrom?: string | null;
  profileStatus?: EcosystemHeadProfileStatus;
  expertiseTags?: string[];
  focusSectors?: EcosystemHeadFocusSector[];
  linkStatus?: string | null;
  logoUrl?: string | null;
  joinRequestLimit?: number | null;
  joinRequestUsed?: number;
  joinRequestRemaining?: number | null;
  joinRequestFull?: boolean;
  joiningCreditsEnabled?: boolean;
  pricingBadge?: CorridorPricingBadgeData | null;
};

type EcosystemHeadCorridorCardProps = {
  head: EcosystemHeadCorridorCardData;
  isSelected?: boolean;
  fullWidth?: boolean;
  /** Navigate via router link (sector carousel, direct profile links). */
  href?: string;
  linkState?: unknown;
  /** Open drawer / custom handler (explore & sector list pages). */
  onClick?: () => void;
  ctaLabel?: string;
  isShortlisted?: boolean;
  showShortlistButton?: boolean;
  onShortlistClick?: () => void | Promise<void>;
  /** Optional override for the explore type badge; defaults from badgeShow / Cluster|Corridor. */
  typeBadge?: string;
  /** Denser type for 3-up dashboard grids. */
  compact?: boolean;
  /** Render inside CorridorCarouselParentCard — no outer chrome / aspect ratio. */
  nested?: boolean;
  onDismissClick?: () => void;
};

const CARD_BLUE = "#2563EB";
/** Reference UI: always show exactly two focus sectors, then "+n more". */
const FOCUS_PREVIEW_COUNT = 2;

export function CorridorCardThumbnail({
  logoUrl,
  title,
}: {
  logoUrl?: string | null;
  title: string;
}) {
  const initial = title.trim().charAt(0).toUpperCase() || "C";

  return (
    <Box className={classes.thumb} aria-hidden>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className={classes.thumbImage}
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "none",
            maxHeight: "none",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      ) : (
        <span className={classes.thumbFallback}>{initial}</span>
      )}
    </Box>
  );
}

function formatIndustrySubtitle(industry: string | null | undefined): string | null {
  const raw = industry?.trim();
  if (!raw) return null;
  return getIndustryLabel(raw) || raw;
}

function CorridorHeaderFlags({
  originCountry,
  targetCountry,
  compact = false,
}: {
  originCountry: string | null | undefined;
  targetCountry: string | null | undefined;
  compact?: boolean;
}) {
  const origin = originCountry?.trim() || null;
  const targetList = normalizeTargetCountryList(targetCountry);
  const target = targetList.length === 1 ? targetList[0] : null;
  const originHasFlag = Boolean(origin && countryHasRenderableFlag(origin));
  const targetHasFlag = Boolean(target && countryHasRenderableFlag(target));
  const flagSize = compact ? "sm" : "md";
  const arrowSize = compact ? 10 : 12;

  if (origin && target && originHasFlag && targetHasFlag) {
    return (
      <Box className={classes.flagPair}>
        <CountryFlag country={origin} size={flagSize} />
        <CorridorDoubleArrow size={arrowSize} color={CARD_BLUE} />
        <CountryFlag country={target} size={flagSize} />
      </Box>
    );
  }

  if (originHasFlag) {
    return (
      <Box className={classes.flagPair}>
        <CountryFlag country={origin!} size={flagSize} />
      </Box>
    );
  }
  if (targetHasFlag) {
    return (
      <Box className={classes.flagPair}>
        <CountryFlag country={target!} size={flagSize} />
      </Box>
    );
  }

  return null;
}

export default function EcosystemHeadCorridorCard({
  head,
  isSelected = false,
  fullWidth = false,
  href,
  linkState,
  onClick,
  ctaLabel,
  isShortlisted = false,
  showShortlistButton = false,
  onShortlistClick,
  typeBadge,
  compact = false,
  nested = false,
  onDismissClick,
}: EcosystemHeadCorridorCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const corridorName = head.corridorName?.trim() || head.corridorLabel;
  const industrySubtitle = formatIndustrySubtitle(head.ecosystemIndustry);
  const joinRequestSlotsLabel = formatJoinRequestSlotsLabel(head);
  const isComingSoon = head.profileStatus === "COMING_SOON";
  const isLive = !isComingSoon;
  const isActive = isHovered || isSelected;
  const isLink = Boolean(href);
  const showBookmark = !isComingSoon && showShortlistButton && Boolean(onShortlistClick);
  const expertiseTag = head.expertiseTags?.find((tag) => tag.trim())?.trim() ?? null;
  const focusSectors = head.focusSectors ?? [];
  const visibleFocus = focusSectors.slice(0, FOCUS_PREVIEW_COUNT);
  const focusRemaining = Math.max(0, focusSectors.length - visibleFocus.length);
  const resolvedCtaLabel = ctaLabel ?? ecosystemHeadExploreCtaLabel(head);
  const resolvedTypeBadge =
    head.badgeShow?.trim() ||
    typeBadge?.trim() ||
    (isEcosystemHeadCorridor(head) ? "Corridor" : "Cluster");

  const viewProfileButton = isComingSoon ? (
    <Box className={classes.ctaComingSoon}>Coming Soon</Box>
  ) : (
    <Box className={`${classes.ctaButton} ${isActive ? classes.ctaButtonActive : ""}`}>
      <span>{resolvedCtaLabel}</span>
      <IconArrowRight size={compact ? 14 : 16} />
    </Box>
  );

  const secondaryActions =
    onDismissClick || showBookmark ? (
      <Group gap={8} wrap="nowrap" align="center">
        {onDismissClick ? <SuggestionDismissButton onClick={onDismissClick} /> : null}
        {showBookmark ? (
          <EcosystemHeadShortlistBookmarkButton
            isShortlisted={isShortlisted}
            onClick={() => onShortlistClick?.()}
            variant="outline"
          />
        ) : null}
      </Group>
    ) : null;

  const wrapperStyle: CSSProperties = {
    flex: nested || fullWidth ? "1 1 auto" : "0 0 auto",
    width: nested || fullWidth ? "100%" : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
    minWidth: ECOSYSTEM_CORRIDOR_EXPLORE_CARD_WIDTH,
    maxWidth: "100%",
    height: "100%",
    minHeight: nested ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
    ...(compact
      ? {
          height: fullWidth ? "100%" : undefined,
          minHeight: fullWidth ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
        }
      : {}),
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
  };

  const cardBody = (
    <Paper
      radius={nested ? "md" : "lg"}
      shadow="none"
      h="100%"
      className={`${classes.card} ${compact ? classes.compact : ""} ${nested ? classes.nested : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: nested
          ? `1px solid ${isSelected ? "#93C5FD" : "transparent"}`
          : `1px solid ${isSelected ? "#60A5FA" : "#E2E8F0"}`,
        boxShadow: nested
          ? "none"
          : isActive
            ? "0 12px 28px rgba(4, 48, 92, 0.14)"
            : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        height: "100%",
        minHeight: nested || compact ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
        flex: 1,
      }}
    >
      <span className={classes.cardHeaderBg} aria-hidden />
      <Box className={classes.cardContent}>
        <Box className={classes.header}>
          <CorridorCardThumbnail logoUrl={head.logoUrl} title={corridorName} />
          <Box className={classes.headerMeta}>
            <Box className={classes.headerTopRow}>
              <EcosystemExploreTypeBadgeRow label={resolvedTypeBadge} embedded />
              {isLive ? (
                <Box className={classes.liveBadge} aria-label="Live">
                  <span className={classes.liveDot} aria-hidden />
                  Live
                </Box>
              ) : null}
            </Box>
            <CorridorHeaderFlags
              originCountry={head.ecosystemCountry}
              targetCountry={head.ecosystemTargetCountry}
              compact={compact}
            />
            <CorridorPricingBadge badge={head.pricingBadge} size={compact ? "xs" : "sm"} />
          </Box>
        </Box>

        <Box className={classes.body}>
          <Text
            className={classes.title}
            fw={800}
            c="#0F172A"
            lh={1.3}
          >
            {corridorName}
          </Text>
          {industrySubtitle ? (
            <Text className={classes.subtitle} fz={compact ? 11 : 13} fw={500} c="#64748B" lh={1.35}>
              {industrySubtitle}
            </Text>
          ) : null}
          {joinRequestSlotsLabel ? (
            <Text
              className={classes.slotLabel}
              fz={compact ? 10 : 11}
              fw={600}
              c={head.joinRequestFull ? "#B91C1C" : "#475569"}
            >
              {joinRequestSlotsLabel}
            </Text>
          ) : null}
        </Box>

        {expertiseTag || visibleFocus.length > 0 ? (
          <Box className={classes.chips}>
            {expertiseTag ? (
              <ExpertiseChip industry={head.ecosystemIndustry} tag={expertiseTag} size={compact ? "sm" : "md"} />
            ) : null}
            {visibleFocus.length > 0 ? (
              <Box w="100%">
                <Text className={classes.focusLabel} fw={800} mb={4}>
                  Focus sectors
                </Text>
                <Box className={classes.focusWrap}>
                  {visibleFocus.map((sector) => (
                    <FocusSectorChip
                      key={`${sector.label}-${sector.icon ?? ""}`}
                      label={sector.label}
                      icon={sector.icon}
                      size="sm"
                    />
                  ))}
                  {focusRemaining > 0 ? (
                    <Box className={classes.moreChip}>+{focusRemaining} more</Box>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Box className={classes.footer}>
          <Box className={classes.ctaRow}>{viewProfileButton}</Box>
          {secondaryActions}
        </Box>
      </Box>
    </Paper>
  );

  const isInteractive = Boolean(href || onClick);

  const handleCardActivate = () => {
    if (href) {
      navigate(href, { state: linkState });
      return;
    }
    onClick?.();
  };

  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest(`[${SUGGESTION_CARD_ACTION_ATTR}]`)) return;
    handleCardActivate();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCardActivate();
  };

  if ((showBookmark || onDismissClick) && isInteractive) {
    return (
      <Box
        style={wrapperStyle}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={0}
      >
        {cardBody}
      </Box>
    );
  }

  if (isLink) {
    return (
      <UnstyledButton component={Link} to={href!} state={linkState} style={wrapperStyle}>
        {cardBody}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton onClick={onClick} style={wrapperStyle}>
      {cardBody}
    </UnstyledButton>
  );
}
